import dayjs from 'dayjs';
import { SharedState } from '$utils/sharedState.svelte';

export interface CacheItem<T> {
  readonly value: StateItem<T>;
  fetched_at: number | null;
}

const DEFAULT_SECONDS_TO_EXPIRE = 60;

export class StateItem<T> extends SharedState<T> {
  get current() {
    return this._current as T;
  }
}

export class SharedCache<K, T> {
  #value: Record<string, CacheItem<T>> = $state({});
  #secondsToExpire: number;

  constructor(secondsToExpire?: number) {
    this.#secondsToExpire = secondsToExpire ?? this.getDefaultExpirationSeconds();
  }

  protected getDefaultExpirationSeconds(): number {
    return DEFAULT_SECONDS_TO_EXPIRE;
  }

  set(_value: Record<string, CacheItem<T>>) {
    this.#value = _value;
  }

  protected keyToString(key: K): string {
    return String(key);
  }

  public invalidate(key: K) {
    const keyStr = this.keyToString(key);
    if (this.#value[keyStr]) {
      this.#value[keyStr] = {
        ...this.#value[keyStr],
        fetched_at: null,
      };
    }
  }

  public async get(
    key: K,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetchData: (key: K, ...args: any[]) => Promise<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
  ): Promise<StateItem<T>> {
    const keyStr = this.keyToString(key);
    const cacheItem = this.#value[keyStr];

    const upToDate =
      cacheItem &&
      cacheItem.fetched_at &&
      dayjs.unix(cacheItem.fetched_at).diff(dayjs(), 'seconds') > -this.getExpirationSeconds();

    if (upToDate) {
      return cacheItem.value;
    }

    const data = await fetchData(key, ...args);

    if (cacheItem && !upToDate) {
      /**
       * Don't create new state, just update existing one:
       */
      this.#value[keyStr].value.set(data);
      this.#value[keyStr].fetched_at = dayjs().unix();
      return this.#value[keyStr].value;
    }

    this.set({
      ...this.#value,
      [keyStr]: {
        value: new StateItem<T>(data),
        fetched_at: dayjs().unix(),
      },
    });

    return this.#value[keyStr].value;
  }

  protected getExpirationSeconds(): number {
    return this.#secondsToExpire;
  }

  public setExpirationSeconds(seconds: number): void {
    this.#secondsToExpire = seconds;
  }
}
